/**
 * Own `user:<presenceId>` topic — the SINGLE owner channel for the logged-in
 * user, held app-wide (mounted in ProtectedRoute).
 *
 * WHY ONE CHANNEL: Phoenix does NOT allow two channels on the same
 * (socket, topic) — a second join CLOSES the first ("Duplicate channel join
 * for topic ... Closing existing channel"). Previously presence
 * (`usePresenceSubscription(selfIds)`) and the messenger-events listener
 * (`useOwnUserMessengerEvents`) each opened their own `user:<id>` channel, so
 * whichever joined last killed the other. Presence won (you showed Online) and
 * the `conversation:activity` listener died — so a brand-new incoming chat only
 * appeared after a manual refresh. This hook owns the topic ONCE and carries
 * BOTH concerns:
 *
 *   1. Presence — as the owner join, the server tracks us online; the attached
 *      `Presence` mirrors our own status back into the presence store (so the
 *      self-status picker reflects cross-tab / in-meeting / offline changes).
 *   2. `conversation:added` — a new DM/group created with me → upsert it.
 *   2b. `conversation:cleared` / `conversation:deleted` — I did that on another
 *      of my devices → drop the thread from this one's cache too.
 *   3. `conversation:activity` — a message in a conversation I'm not yet
 *      subscribed to (someone started a new chat) → fetch + append so it shows
 *      live without a refresh.
 *
 * Status writes stay on REST (`presenceApi.setMe`) — no channel push here.
 * `usePresenceSubscription` is now used ONLY to WATCH OTHER users, never our
 * own id, so the two never collide.
 */

import { useEffect } from 'react'
import { Presence } from 'phoenix'
import { useAuthStore } from '@/features/auth/store/auth.store'
import { getPhoenixSocket } from '@/lib/phoenix-socket'
import { getDeviceId } from '@/lib/device-id'
import {
  aggregate,
  type RawMeta,
} from '@/features/presence/hooks/usePresenceSubscription'
import { usePresenceStore } from '@/features/presence/store/presence.store'
import type { Conversation as WireConversation, Message as WireMessage } from '../services/messenger.wire'
import { messengerApi } from '../services/messenger.api'
import { useMessengerStore } from '../store/messenger.store'
import { purgeConversation, purgeConversationMessages } from '../services/messenger.db'
import { _forgetMessageSeed } from './useConversationMessages'
import { CallsModule, type OwnerChannelHost, type CallSession } from '@/sdk/instavcMessenger'
import { useCallsStore } from '../store/calls.store'

export function useOwnUserChannel(): void {
  const me = useAuthStore((s) => s.user)
  // The own topic is keyed by the per-org `contactPresenceId`; the socket still
  // authenticates as the auth user (`me.id`). Null until resolved/consented.
  const presenceId = useAuthStore((s) => s.presenceId)

  useEffect(() => {
    if (!me?.id || !presenceId) return
    let cancelled = false
    let teardown: (() => void) | null = null

    void (async () => {
      try {
        const socket = await getPhoenixSocket(me.id, me.name)
        if (cancelled) return

        const channel = socket.channel(`user:${presenceId}`, {})

        // (1) Presence — mirror my own status back into the store. Attached
        // BEFORE join so the initial `presence_state` push isn't missed.
        const presence = new Presence(channel)
        presence.onSync(() => {
          const list = presence.list<{ key: string; metas: RawMeta[] }>(
            (key, p) => ({ key, metas: p.metas as RawMeta[] }),
          )
          const found = list.find((row) => row.key === presenceId)
          usePresenceStore.getState().setOne(aggregate(presenceId, found?.metas ?? []))
        })
        channel.on('presence:offline_confirmed', (payload: { user_id: string; last_seen_at: string }) => {
          usePresenceStore.getState().markOffline(payload.user_id, payload.last_seen_at)
        })

        // (2) A new conversation was created with me.
        channel.on('conversation:added', (payload: { conversation: WireConversation }) => {
          useMessengerStore.getState().upsertWireConversation(payload.conversation)
        })

        /**
         * (2b) I cleared or deleted this chat on ANOTHER of my devices.
         *
         * The server-side floor already hides the messages from every read, so
         * without this the thread would still look intact here until something
         * forced a history fetch — the local-first cache has no reason to
         * re-ask. Purging IndexedDB is the point of the handler; the store
         * mutation is just what makes it visible now.
         *
         * The device that performed the action also receives its own event.
         * Re-clearing an already-cleared thread is a no-op, so that costs
         * nothing and saves threading a device id through the whole path.
         */
        channel.on('conversation:cleared', (payload: { conversationId: string }) => {
          const convId = payload?.conversationId
          if (!convId) return
          useMessengerStore.getState().clearWireMessages(convId)
          void purgeConversationMessages(convId)
          _forgetMessageSeed(convId)
        })

        channel.on('conversation:deleted', (payload: { conversationId: string }) => {
          const convId = payload?.conversationId
          if (!convId) return
          useMessengerStore.getState().removeWireConversation(convId)
          void purgeConversation(convId)
          _forgetMessageSeed(convId)
        })

        // (3) A message landed in a conversation I'm not subscribed to yet
        // (someone started a new chat). `conversation:added` isn't emitted on
        // the SDK create path, so this nudge is how a brand-new thread shows up
        // live: fetch it, add it (which makes the list subscription join its
        // channel) and append the message. Known conversations are handled by
        // their own channel, so skip those.
        channel.on('conversation:activity', (payload: { conversationId: string; message?: WireMessage }) => {
          const convId = payload?.conversationId
          if (!convId) return
          const store = useMessengerStore.getState()

          /**
           * Append FIRST, and unconditionally.
           *
           * This nudge is the ONLY delivery path for a message that landed
           * before we joined that conversation's own channel — which is always
           * true of the first message from a new contact. The append used to
           * sit inside the "conversation is unknown" branch, so whether the
           * message arrived depended on a fact about the CONVERSATION rather
           * than about the message.
           *
           * That lost it whenever `conversation:added` (2) won the race and
           * added the row a moment earlier: this handler saw a known
           * conversation and returned before appending. The conversation still
           * carried `lastMessage`, so the chat list showed a preview over a
           * thread with nothing in it — and only the FIRST message, because by
           * the second one the channel subscription had joined.
           *
           * Safe to do eagerly: `appendWireMessage` dedupes by message id, so
           * a later delivery on the conversation channel is a no-op, and it
           * does not need the conversation row to exist yet.
           */
          if (payload.message) store.appendWireMessage(payload.message)

          // Known conversations need nothing further — their own channel and
          // the list already cover them.
          if (store.wireConversations.has(convId)) return

          void messengerApi
            .getConversation(convId)
            .then(({ conversation }) => {
              // Adding it makes the list subscription join its channel, so
              // everything after this arrives the ordinary way.
              useMessengerStore.getState().upsertWireConversation(conversation)
            })
            .catch(() => {
              /* best-effort — the next list refresh will surface it */
            })
        })

        // (4) 1:1 call ring — attach the SDK's CallsModule onto THIS owner
        // channel (we can't let the SDK's PresenceModule join `user:<id>` too —
        // Phoenix would close one of them). A tiny adapter exposes the channel
        // as an OwnerChannelHost; the module binds its `call:*` handlers before
        // join (so nothing pushed on connect is missed) and drives the UI store.
        const host: OwnerChannelHost = {
          onOwnerChannel: (fn) => fn(channel),
          pushOnOwner: (event, payload) =>
            new Promise((resolve, reject) => {
              channel
                .push(event, payload)
                .receive('ok', resolve)
                .receive('error', reject)
                .receive('timeout', () => reject(new Error(`${event} timed out`)))
            }),
        }
        const calls = new CallsModule(host, getDeviceId())
        calls.start()
        const offIncoming = calls.on('incoming', (c) => useCallsStore.getState()._onIncoming(c as CallSession))
        const offState = calls.on('stateChange', (c) => useCallsStore.getState()._onStateChange(c as CallSession))
        useCallsStore.getState().attach(calls)

        channel
          .join()
          .receive('error', () => {
            // Self-join shouldn't be forbidden — but defend anyway. Leave to
            // cancel phoenix-js's rejoin backoff.
            try { channel.leave() } catch {/* ignore */}
          })

        teardown = () => {
          try { offIncoming(); offState(); calls.teardown() } catch {/* ignore */}
          useCallsStore.getState().detach()
          try { channel.leave() } catch {/* ignore */}
        }
      } catch {
        /* socket teardown / token miss — bail silently */
      }
    })()

    return () => {
      cancelled = true
      teardown?.()
    }
  }, [me?.id, me?.name, presenceId])
}
